// Byte/string encoding helpers and the on-the-wire format for encrypted values.
//
// Every ciphertext Agile Vault stores is serialised as an "EncString": a short,
// self-describing, dot-separated string. Encoding the algorithm inline means a
// stored blob can never be decrypted under the wrong scheme by accident, and
// makes migrations (new algorithm versions) unambiguous.

// WebCrypto (TS 5.7+) requires ArrayBuffer-backed views, not the wider
// `ArrayBufferLike`. Every byte value we hand to subtle crypto uses this alias.
export type Bytes = Uint8Array<ArrayBuffer>;

export type EncType =
  | "A1" // AES-256-GCM: A1.<iv>.<ciphertext+tag>
  | "R1"; // RSA-OAEP-2048/SHA-256: R1.<ciphertext>

export interface Parsed {
  type: EncType;
  iv: Bytes | null;
  ct: Bytes;
}

const encoder = new TextEncoder();
const decoder = new TextDecoder();

export const utf8 = (s: string): Bytes => encoder.encode(s) as Bytes;
export const fromUtf8 = (b: Uint8Array): string => decoder.decode(b);

/** Cryptographically strong random bytes. */
export function randomBytes(length: number): Bytes {
  const b = new Uint8Array(length);
  crypto.getRandomValues(b);
  return b;
}

export function toB64(bytes: Uint8Array): string {
  let s = "";
  for (const b of bytes) s += String.fromCharCode(b);
  return btoa(s);
}

export function fromB64(s: string): Bytes {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/** Serialise an encrypted value to its EncString form. */
export function encodeEnc(
  type: EncType,
  iv: Uint8Array | null,
  ct: Uint8Array,
): string {
  return type === "A1"
    ? `A1.${toB64(iv!)}.${toB64(ct)}`
    : `R1.${toB64(ct)}`;
}

/** Parse an EncString, validating its shape for the declared algorithm. */
export function decodeEnc(value: string): Parsed {
  const parts = value.split(".");
  const type = parts[0];
  if (type === "A1") {
    if (parts.length !== 3 || !parts[1] || !parts[2]) {
      throw new Error("malformed A1 EncString");
    }
    return { type, iv: fromB64(parts[1]), ct: fromB64(parts[2]) };
  }
  if (type === "R1") {
    if (parts.length !== 2 || !parts[1]) {
      throw new Error("malformed R1 EncString");
    }
    return { type, iv: null, ct: fromB64(parts[1]) };
  }
  throw new Error(`unknown EncString type: ${String(type)}`);
}
